import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Archive,
  Clear,
  Delete,
  Edit,
  MoreVert,
  PlayArrow,
  Publish,
  Refresh,
  Search,
  Unpublished,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import MediaEditorDialog from "./MediaEditorDialog";
import MediaPreviewDialog from "./MediaPreviewDialog";
import { mediaApi } from "./mediaApi";
import {
  assetStatusMessage,
  canDeleteMedia,
  canPublishMedia,
  formatDuration,
  formatMediaDate,
  getPublishDisabledReason,
} from "./media.utils";
import "./media.scss";

const publicationStatuses = ["draft", "published", "archived"];
const assetStatuses = ["pending", "uploaded", "processing", "ready", "failed"];

const assetStatusSummary = (status) => ({
  pending: "Awaiting verified media",
  uploaded: "Verification queued",
  processing: "Verifying uploads",
  ready: "Media verified",
  failed: "Verification failed",
}[status] || "Status unavailable");

const formatSection = (section) =>
  section ? section[0].toUpperCase() + section.slice(1) : "Needs review";

const formatChannel = (channel) => {
  if (channel === "spaceas") return "SPACEAS";
  if (channel === "leaderbridge") return "LeaderBridge";
  return channel;
};

const formatFullTimestamp = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const getDateDetails = (media) => [
  `Created: ${formatFullTimestamp(media.createdAt)}`,
  `Updated: ${formatFullTimestamp(media.updatedAt)}`,
  `Published: ${formatFullTimestamp(media.publishedAt)}`,
].join(" · ");

const Thumbnail = ({ media, posterUrl }) => (
  <Box className={`media-thumbnail media-thumbnail--${media.kind}`}>
    {posterUrl ? (
      <img src={posterUrl} alt={media.poster?.alt || `${media.title} poster`} />
    ) : (
      <>
        <span className={media.poster?.verifiedAt ? "fas fa-image" : "fas fa-lock"} aria-hidden="true" />
        <span>{media.poster?.verifiedAt ? "Verified poster" : "No public thumbnail"}</span>
      </>
    )}
  </Box>
);

const Status = ({ value }) => (
  <span className={`media-status media-status--${value}`}>{value || "unknown"}</span>
);

const Actions = ({ media, busy, context, onEdit, onPreview, onAction, onDelete }) => {
  const [anchorElement, setAnchorElement] = useState(null);
  const menuOpen = Boolean(anchorElement);
  const menuId = `media-actions-${context}-${media._id}`;
  const closeMenu = () => setAnchorElement(null);
  const chooseAction = (action) => {
    closeMenu();
    onAction(action, media);
  };
  const chooseDelete = () => {
    closeMenu();
    onDelete(media);
  };

  return (
    <Box className="media-row-actions">
      <Tooltip title="Edit content, media, and featured settings">
        <span><IconButton size="small" onClick={() => onEdit(media)} disabled={busy || media.status === "archived"} aria-label={`Edit ${media.title}`}><Edit /></IconButton></span>
      </Tooltip>
      <Tooltip title="Preview">
        <span><IconButton size="small" onClick={() => onPreview(media)} disabled={busy} aria-label={`Preview ${media.title}`}><PlayArrow /></IconButton></span>
      </Tooltip>
      <Tooltip title="More actions">
        <span>
          <IconButton
            size="small"
            onClick={(event) => setAnchorElement(event.currentTarget)}
            disabled={busy}
            aria-label={`More actions for ${media.title}`}
            aria-controls={menuOpen ? menuId : undefined}
            aria-expanded={menuOpen ? "true" : undefined}
            aria-haspopup="menu"
          >
            <MoreVert />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        id={menuId}
        className="media-action-menu"
        anchorEl={anchorElement}
        open={menuOpen}
        onClose={closeMenu}
        MenuListProps={{ "aria-label": `Actions for ${media.title}` }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {media.status === "draft" && (
          <MenuItem
            onClick={() => chooseAction("publish")}
            disabled={busy || !canPublishMedia(media)}
            title={getPublishDisabledReason(media)}
          >
            <Publish fontSize="small" aria-hidden="true" />
            <span>Publish</span>
          </MenuItem>
        )}
        {media.status === "published" && (
          <MenuItem onClick={() => chooseAction("unpublish")} disabled={busy}>
            <Unpublished fontSize="small" aria-hidden="true" />
            <span>Unpublish</span>
          </MenuItem>
        )}
        {media.status !== "archived" && (
          <MenuItem onClick={() => chooseAction("archive")} disabled={busy}>
            <Archive fontSize="small" aria-hidden="true" />
            <span>Archive</span>
          </MenuItem>
        )}
        <MenuItem
          className="media-action-delete"
          onClick={chooseDelete}
          disabled={busy || !canDeleteMedia(media)}
          title={canDeleteMedia(media) ? "Delete empty draft" : "Only drafts without retained uploaded assets can be deleted"}
        >
          <Delete fontSize="small" aria-hidden="true" />
          <span>Delete</span>
        </MenuItem>
      </Menu>
    </Box>
  );
};

const MediaContent = ({ media, posterUrl }) => {
  const destinations = (media.channels || []).map(formatChannel).join(", ");
  return (
    <Box className="media-content-cell">
      <Thumbnail media={media} posterUrl={posterUrl} />
      <Box className="media-content-copy">
        <strong title={media.title}>{media.title}</strong>
        <Box className="media-content-meta">
          <span>{media.kind === "short" ? "Short" : "Video"}</span>
          {destinations && <><span aria-hidden="true">·</span><span title={destinations}>{destinations}</span></>}
        </Box>
      </Box>
    </Box>
  );
};

const UpdatedDate = ({ media }) => {
  const value = media.updatedAt || media.publishedAt || media.createdAt;
  return (
    <Tooltip title={getDateDetails(media)}>
      <time dateTime={value || undefined}>{formatMediaDate(value)}</time>
    </Tooltip>
  );
};

const MediaManagementPage = ({ kind }) => {
  const requestIdRef = useRef(0);
  const plural = kind === "short" ? "Shorts" : "Videos";
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [filters, setFilters] = useState({ page: 1, limit: 12, status: "", assetStatus: "" });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [authorizedPreviews, setAuthorizedPreviews] = useState({});
  const [editor, setEditor] = useState({ open: false, media: null });
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => () => {
    requestIdRef.current += 1;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 450);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setFilters((current) => ({ ...current, page: 1 }));
  }, [search]);

  const loadMedia = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError("");
    setAuthorizedPreviews({});
    try {
      const result = await mediaApi.list({ ...filters, kind, search });
      if (requestId !== requestIdRef.current) return;
      setItems(result.items || []);
      setPagination(result.pagination || { page: filters.page, totalPages: 0, total: 0 });

      const previewRequests = (result.items || [])
        .filter((item) => item.status === "published" && item.slug && item.channels?.length)
        .map(async (item) => {
          try {
            const { media: publicMedia } = await mediaApi.publicPreview(item.slug, item.channels[0]);
            return [item._id, publicMedia];
          } catch {
            return [item._id, null];
          }
        });
      Promise.all(previewRequests).then((entries) => {
        if (requestId === requestIdRef.current) {
          setAuthorizedPreviews(Object.fromEntries(entries.filter((entry) => entry[1])));
        }
      });
    } catch (loadError) {
      if (requestId !== requestIdRef.current) return;
      setError(loadError.message);
      setItems([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [filters, kind, search]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const updateFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value, page: 1 }));
  };

  const requestAction = (action, media) => {
    const labels = {
      publish: ["Publish media?", "This makes the verified media available at its selected destinations."],
      unpublish: ["Unpublish media?", "This immediately removes it from the public media API and returns it to draft."],
      archive: ["Archive media?", "Archived media cannot be edited or restored with the current backend API."],
    };
    setConfirmation({ action, media, title: labels[action][0], copy: labels[action][1] });
  };

  const requestDelete = (media) => {
    setConfirmation({
      action: "remove",
      media,
      title: "Delete media draft?",
      copy: "This permanently deletes the empty draft. Uploaded or retained assets prevent deletion.",
    });
  };

  const performConfirmedAction = async () => {
    const current = confirmation;
    if (!current || busyId) return;
    setBusyId(current.media._id);
    setError("");
    try {
      await mediaApi[current.action](current.media._id);
      toast.success(current.action === "remove" ? "Media draft deleted." : `Media ${current.action} action completed.`);
      setConfirmation(null);
      await loadMedia();
    } catch (actionError) {
      setError(actionError.message);
      setConfirmation(null);
    } finally {
      setBusyId("");
    }
  };

  const handleSaved = useCallback(() => loadMedia(), [loadMedia]);

  return (
    <div className="media-management-page">
      <Container maxWidth={false} className="media-management-container">
        <Box className="media-management-header">
          <Box>
            <Typography variant="h3" component="h1">{plural}</Typography>
            <Typography color="textSecondary">
              Manage {kind === "short" ? "short-form" : "long-form"} video content and publication.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => setEditor({ open: true, media: null })}>
            Create {kind === "short" ? "Short" : "Video"}
          </Button>
        </Box>

        <Box className="media-filters">
          <TextField
            className="media-search"
            label={`Search ${plural}`}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search by title, topic, or tag"
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
              endAdornment: searchInput ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearchInput("")} aria-label="Clear search"><Clear /></IconButton></InputAdornment> : null,
            }}
          />
          <FormControl className="media-filter-select">
            <InputLabel id="publication-status-label">Publication</InputLabel>
            <Select labelId="publication-status-label" label="Publication" value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>
              <MenuItem value="">All</MenuItem>
              {publicationStatuses.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl className="media-filter-select">
            <InputLabel id="asset-status-label">Asset status</InputLabel>
            <Select labelId="asset-status-label" label="Asset status" value={filters.assetStatus} onChange={(event) => updateFilter("assetStatus", event.target.value)}>
              <MenuItem value="">All</MenuItem>
              {assetStatuses.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
            </Select>
          </FormControl>
          <Tooltip title="Refresh statuses"><span><IconButton onClick={loadMedia} disabled={loading} aria-label="Refresh media list"><Refresh /></IconButton></span></Tooltip>
        </Box>

        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
        {loading ? (
          <Box className="media-loading"><CircularProgress /><Typography>Loading {plural.toLowerCase()}…</Typography></Box>
        ) : items.length === 0 ? (
          <Box className="media-empty">
            <Typography variant="h5">No {plural.toLowerCase()} found</Typography>
            <Typography color="textSecondary">Adjust the filters or create a new draft.</Typography>
          </Box>
        ) : (
          <>
            <Box className="media-table-wrap">
              <table className="media-table">
                <caption className="media-visually-hidden">Manage {plural.toLowerCase()}</caption>
                <colgroup>
                  <col className="media-column-content" />
                  <col className="media-column-details" />
                  <col className="media-column-section" />
                  <col className="media-column-publication" />
                  <col className="media-column-status" />
                  <col className="media-column-updated" />
                  <col className="media-column-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Content</th>
                    <th scope="col">Details</th>
                    <th scope="col">Section</th>
                    <th scope="col">Publication</th>
                    <th scope="col">Media status</th>
                    <th scope="col">Updated</th>
                    <th scope="col" className="media-actions-heading">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((media) => (
                    <tr key={media._id}>
                      <td><MediaContent media={media} posterUrl={authorizedPreviews[media._id]?.poster?.url} /></td>
                      <td>
                        <span className="media-detail-primary">{formatDuration(media.video?.durationSeconds)}</span>
                        <small className="media-detail-secondary">{media.topic || "No topic"}</small>
                      </td>
                      <td><span className="media-section-label">{formatSection(media.section)}</span></td>
                      <td>
                        <Status value={media.status} />
                        {media.featured && (
                          <small className="media-featured-label">
                            Featured{media.featuredOrder !== null && media.featuredOrder !== undefined ? ` · ${media.featuredOrder}` : ""}
                          </small>
                        )}
                      </td>
                      <td>
                        <Status value={media.assetStatus} />
                        <small className="media-asset-summary" title={assetStatusMessage(media.assetStatus)}>
                          {assetStatusSummary(media.assetStatus)}
                        </small>
                      </td>
                      <td className="media-updated-cell"><UpdatedDate media={media} /></td>
                      <td><Actions context="table" media={media} busy={busyId === media._id} onEdit={(value) => setEditor({ open: true, media: value })} onPreview={setPreview} onAction={requestAction} onDelete={requestDelete} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            <Box className="media-card-list">
              {items.map((media) => (
                <article className="media-card" key={media._id}>
                  <Box className="media-card-primary">
                    <Thumbnail media={media} posterUrl={authorizedPreviews[media._id]?.poster?.url} />
                    <Box className="media-card-heading">
                      <Typography variant="h6">{media.title}</Typography>
                      <Typography variant="caption">
                        {media.kind === "short" ? "Short" : "Video"}
                        {(media.channels || []).length ? ` · ${media.channels.map(formatChannel).join(", ")}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="media-card-body">
                    <Box className="media-card-statuses">
                      <Box><span>Publication</span><Status value={media.status} /></Box>
                      <Box><span>Media</span><Status value={media.assetStatus} /></Box>
                    </Box>
                    <Typography className="media-card-status-copy" variant="caption">{assetStatusSummary(media.assetStatus)}</Typography>
                    <dl>
                      <div><dt>Section</dt><dd>{formatSection(media.section)}</dd></div>
                      <div><dt>Duration</dt><dd>{formatDuration(media.video?.durationSeconds)}</dd></div>
                      <div><dt>Topic</dt><dd>{media.topic || "—"}</dd></div>
                      <div><dt>Updated</dt><dd><UpdatedDate media={media} /></dd></div>
                      {media.featured && <div><dt>Featured</dt><dd>{media.featuredOrder !== null && media.featuredOrder !== undefined ? `Order ${media.featuredOrder}` : "Yes"}</dd></div>}
                    </dl>
                    <Actions context="card" media={media} busy={busyId === media._id} onEdit={(value) => setEditor({ open: true, media: value })} onPreview={setPreview} onAction={requestAction} onDelete={requestDelete} />
                  </Box>
                </article>
              ))}
            </Box>
          </>
        )}

        {pagination.totalPages > 1 && (
          <Box className="media-pagination">
            <Typography variant="body2">{pagination.total} records</Typography>
            <Pagination count={pagination.totalPages} page={pagination.page || filters.page} onChange={(event, page) => setFilters((current) => ({ ...current, page }))} color="primary" />
          </Box>
        )}
      </Container>

      <MediaEditorDialog open={editor.open} kind={kind} initialMedia={editor.media} onClose={() => setEditor({ open: false, media: null })} onSaved={handleSaved} />
      <MediaPreviewDialog open={Boolean(preview)} media={preview} onClose={() => setPreview(null)} />
      <Dialog open={Boolean(confirmation)} onClose={busyId ? undefined : () => setConfirmation(null)} aria-labelledby="media-confirm-title">
        <DialogTitle id="media-confirm-title">{confirmation?.title}</DialogTitle>
        <DialogContent><Typography>{confirmation?.copy}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmation(null)} disabled={Boolean(busyId)}>Cancel</Button>
          <Button variant="contained" color={confirmation?.action === "remove" ? "error" : "primary"} onClick={performConfirmedAction} disabled={Boolean(busyId)}>{busyId ? "Working…" : "Confirm"}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MediaManagementPage;
